import type {Placement} from '@floating-ui/dom';
import type {
  Cmd,
  ImmutableProgram,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {FlywheelComponent} from '@gl/lit/lib/component.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type FlyFloatingSurfaceV2 from './index.js';

export const DEFAULT_PLACEMENT = 'bottom';

type ComponentProperties<Component extends FlywheelComponent> = {
  get<K extends keyof Component>(key: K): Component[K];
};

export type Model = {
  visible: boolean;
  placement: Placement;
  hideOnClickOutside: boolean;
  anchorElement: Element | null;
};

export const Msg = makeVariants({
  show: Variant<void>()('show'),
  hide: Variant<void>()('hide'),
  setPlacement: Variant<Placement>()('set-placement'),
  setClickOutsideBehaviour: Variant<boolean>()('set-click-outside-behaviour'),
  registerAnchor: Variant<Element>()('register-anchor'),
  unregisterAnchor: Variant<void>()('unregister-anchor'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(
  props: ComponentProperties<FlyFloatingSurfaceV2>
): Readonly<Model> {
  return {
    visible: props.get('visible') ?? false,
    placement: props.get('placement') ?? DEFAULT_PLACEMENT,
    hideOnClickOutside: props.get('closeWhenClickOutside') ?? false,
    anchorElement: props.get('anchor') ?? null,
  } as const;
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'show':
      return [
        {...model, visible: true},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.onShow();
          host.handleAnchorPosition();
          host.updateClickOutsideBehaviour(model.hideOnClickOutside);
        }),
      ] as const;
    case 'hide':
      return [
        {...model, visible: false},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.handleAnchorPosition();
          host.onHide();
          host.updateClickOutsideBehaviour(model.hideOnClickOutside);
        }),
      ] as const;
    case 'set-placement':
      return [
        {...model, placement: msg.value},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.handleAnchorPosition();
        }),
      ] as const;
    case 'set-click-outside-behaviour':
      return [
        {...model, hideOnClickOutside: msg.value},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.updateClickOutsideBehaviour(model.visible && msg.value);
        }),
      ] as const;
    case 'register-anchor': {
      const el = msg.value;
      const oldValue = model.anchorElement;
      return [
        {...model, anchorElement: el},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.handleAnchorPosition();
          if (el !== oldValue) {
            host.registerAnchorInController(el);
          }
        }),
      ] as const;
    }
    case 'unregister-anchor': {
      const oldValue = model.anchorElement;
      return [
        {...model, anchorElement: null},
        cmd((host: FlyFloatingSurfaceV2) => {
          host.handleAnchorPosition();
          if (oldValue) {
            host.removeAnchorInController(oldValue);
          }
        }),
      ] as const;
    }
  }
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init: init as ImmutableProgram<Model, Msg>['init'],
  update,
};
